/**
 * A ranked "who / which" list inside a drawer: one row per person or template,
 * with its requests and its share of the drawer's total.
 *
 * Built to prove itself: the footer line adds the rows up ("12 people · 100
 * requests") right under the figure the table showed, and if the two differ
 * (requests raised or removed between the table loading and the drawer opening)
 * it says so rather than leaving the reader to spot a gap. Single-hue share
 * bars, not a stacked colour bar: past five or six names a categorical palette
 * stops being readable, and a ranked list never does.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, AppWindow, ArrowUpRight, ChevronRight, Layers, Search, UserX } from 'lucide-react'
import UserAvatar from '../UserAvatar'
import { ShareBar } from './BillingPrimitives'
import { formatInt } from '../../utils/billing'
import { personDetail, personName } from '../../utils/requestBreakdown'

const SHOWN = 8           // rows before "Show all"
const SEARCH_FROM = 12    // a search box once the list is longer than this

const templateName = r => r.template_name

const plural = (n, one, many) => `${formatInt(n)} ${n === 1 ? one : many}`

function PersonMark({ row }) {
  if (row.kind === 'service') {
    return (
      <span className="w-8 h-8 flex-shrink-0 rounded-full bg-navy-50 dark:bg-navy-900/40 flex items-center justify-center
                       text-navy-600 dark:text-navy-300">
        <AppWindow className="w-3.5 h-3.5" />
      </span>
    )
  }
  if (row.kind === 'unknown' || (row.user_id ?? null) === null) {
    return (
      <span className="w-8 h-8 flex-shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center
                       text-gray-400 dark:text-gray-500">
        <UserX className="w-3.5 h-3.5" />
      </span>
    )
  }
  return <UserAvatar user={{ id: row.user_id, username: row.username, email: row.email }} size="sm" />
}

function TemplateMark() {
  return (
    <span className="w-8 h-8 flex-shrink-0 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center
                     text-gray-500 dark:text-gray-400">
      <Layers className="w-3.5 h-3.5" />
    </span>
  )
}

/**
 * @param kind 'people' | 'templates'
 * @param rows from raisedBy / templatesUsed / everyoneElse (each with requests and sharePct)
 * @param total the rows' own sum
 * @param expectedTotal the figure the table showed for this row, if any
 * @param onSelect people only: makes each row open that person
 */
export default function BreakdownList({
  title, kind, rows = [], total = 0, expectedTotal = null, loading = false, error = null, onRetry, onSelect, emptyText,
}) {
  const [expanded, setExpanded] = useState(false)
  const [query, setQuery] = useState('')
  const isPeople = kind === 'people'
  const nameOf = isPeople ? personName : templateName

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r => `${nameOf(r)} ${isPeople ? r.email || '' : ''}`.toLowerCase().includes(q))
  }, [rows, query, isPeople, nameOf])

  const visible = expanded || query ? filtered : filtered.slice(0, SHOWN)
  const max = Math.max(1, ...rows.map(r => r.requests))
  const unit = isPeople ? ['person', 'people'] : ['template', 'templates']

  const heading = (
    <div className="flex items-baseline justify-between gap-3 mb-2">
      <h3 className="text-[12px] font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {!loading && !error && rows.length > 0 && (
        <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
          {plural(rows.length, ...unit)} · {plural(total, 'request', 'requests')}
        </span>
      )}
    </div>
  )

  if (loading) {
    return (
      <section aria-busy="true">
        {heading}
        <div className="space-y-2">
          {[0, 1, 2, 3].map(i => <div key={i} className="skeleton h-10 rounded-lg" />)}
        </div>
        <span className="sr-only" role="status">Loading {title.toLowerCase()}</span>
      </section>
    )
  }

  if (error) {
    return (
      <section>
        {heading}
        <div className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5
                        text-[12px] text-gray-600 dark:text-gray-300">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
          <p className="flex-1">This breakdown couldn&apos;t be loaded. The figures above are unaffected.</p>
          {onRetry && (
            <button type="button" onClick={onRetry}
              className="text-[12px] font-semibold text-navy-700 dark:text-navy-300 underline underline-offset-2
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded">
              Try again
            </button>
          )}
        </div>
      </section>
    )
  }

  if (rows.length === 0) {
    return (
      <section>
        {heading}
        <p className="text-[12px] text-gray-500 dark:text-gray-400 py-3">{emptyText}</p>
      </section>
    )
  }

  const drift = expectedTotal !== null && expectedTotal !== undefined ? total - expectedTotal : 0

  return (
    <section>
      {heading}

      {rows.length > SEARCH_FROM && (
        <label className="relative block mb-2">
          <span className="sr-only">Search {title.toLowerCase()}</span>
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={isPeople ? 'Search people' : 'Search templates'}
            className="w-full h-8 pl-8 pr-2 text-[12px] rounded-md border border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          />
        </label>
      )}

      <ol className="divide-y divide-gray-100 dark:divide-gray-800/60">
        {visible.map(row => {
          const key = isPeople ? `u:${row.user_id ?? 'none'}` : `t:${row.template_id ?? 'none'}`
          const detail = isPeople ? personDetail(row) : (row.template_id ? `Template #${row.template_id}` : 'Requests with no template')
          const body = (
            <>
              {isPeople ? <PersonMark row={row} /> : <TemplateMark />}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-[13px] font-medium text-gray-900 dark:text-gray-100">
                  <span className="truncate">{nameOf(row)}</span>
                  {isPeople && row.kind === 'service' && (
                    <span className="flex-shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wider
                                     bg-navy-50 text-navy-700 dark:bg-navy-900/40 dark:text-navy-300">App</span>
                  )}
                  {isPeople && row.is_active === false && (
                    <span className="flex-shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wider
                                     bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">Deactivated</span>
                  )}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{detail}</p>
              </div>
              <div className="w-[132px] flex-shrink-0">
                <p className="text-right text-[13px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                  {formatInt(row.requests)}
                </p>
                <ShareBar value={row.requests} max={max} label={`${row.sharePct}%`} />
              </div>
            </>
          )
          const rowClass = 'flex items-center gap-2.5 py-2'
          const interactive = `${rowClass} -mx-2 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-500`
          if (isPeople && onSelect && (row.user_id ?? null) !== null) {
            return (
              <li key={key}>
                <button type="button" onClick={() => onSelect(row)} className={`w-full text-left ${interactive}`}
                  aria-label={`${nameOf(row)}: ${formatInt(row.requests)} requests. View their templates`}>
                  {body}
                  <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                </button>
              </li>
            )
          }
          if (!isPeople && row.template_id && !String(row.template_name).startsWith('Deleted Template')) {
            return (
              <li key={key}>
                <Link to={`/templates/${row.template_id}`} className={interactive}
                  aria-label={`${row.template_name}: ${formatInt(row.requests)} requests. Open this template`}>
                  {body}
                  <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                </Link>
              </li>
            )
          }
          return <li key={key} className={rowClass}>{body}</li>
        })}
      </ol>

      {query && filtered.length === 0 && (
        <p className="text-[12px] text-gray-500 dark:text-gray-400 py-3">No match for &ldquo;{query}&rdquo;.</p>
      )}

      {!query && filtered.length > SHOWN && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="mt-2 text-[12px] font-semibold text-navy-700 dark:text-navy-300 hover:underline underline-offset-2
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
        >
          {expanded ? 'Show fewer' : `Show all ${plural(rows.length, ...unit)}`}
        </button>
      )}

      {drift !== 0 && (
        <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-400">
          The table counted {plural(expectedTotal, 'request', 'requests')}; {formatInt(Math.abs(drift))}{' '}
          {Math.abs(drift) === 1 ? 'was' : 'were'} {drift > 0 ? 'raised' : 'removed'} since it loaded.
          Refresh the page figures to bring both up to date.
        </p>
      )}
    </section>
  )
}
